import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { getDefaultPriceObject } from "../../../../../common-deprecated/constants";
import type { ExternalPriceCalulatedPriceType } from "../../../../shared-logic/types/externalPriceTypes";
import type { PriceType } from "../../../../../common-deprecated/types/CommonTypes";
import { RequestStatusEnum } from "../../../../../common-deprecated/types/CommonTypes";
import type { CO2SpecsType } from "../../../../shared-logic/types/ConfigurationSpecificationsObjectType";

export type CarConfigExternalPriceStateType = {
    state: RequestStatusEnum;
    calculatedPrice: ExternalPriceCalulatedPriceType;
    originalTotalPrice: PriceType;
    co2Specs: CO2SpecsType;
    hadError: boolean;
    taxLocationId: string | null;
};

export const initialState: CarConfigExternalPriceStateType = {
    state: RequestStatusEnum.Idle,
    calculatedPrice: {
        totalValue: 0,
        carPrice: null,
        tax: 0,
        totalValueWithoutTax: 0,
        extraCosts: [],
        calculatedRawData: {},
    },
    originalTotalPrice: { ...getDefaultPriceObject() },
    co2Specs: { unit: "", value: "" },
    hadError: false,
    taxLocationId: "",
};

export type CarConfigExternalPriceActionsType = ReturnType<
    | typeof enableExternalPrice
    | typeof startExternalPriceValueFetch
    | typeof finishExternalPriceValueFetch
    | typeof externalPriceSetError
    | typeof setExternalPriceTaxLocation
    | typeof externalPriceReset
>;

export const CarConfigExternalPriceSlice = createSlice({
    name: "externalPrice",
    initialState,
    reducers: {
        enableExternalPrice(state, action: PayloadAction<boolean>) {
            return {
                ...state,
                enabled: action.payload,
            };
        },
        startExternalPriceValueFetch(state, action: PayloadAction<{ originalTotalPrice?: PriceType }>) {
            return {
                ...state,
                state: RequestStatusEnum.Fetching,
                originalTotalPrice: action.payload.originalTotalPrice || state.originalTotalPrice,
                hadError: false,
            };
        },
        finishExternalPriceValueFetch(
            state,
            action: PayloadAction<{ calculatedPrice: ExternalPriceCalulatedPriceType; co2Specs: CO2SpecsType | null }>,
        ) {
            return {
                ...state,
                state: RequestStatusEnum.Complete,
                calculatedPrice: { ...action.payload.calculatedPrice },
                co2Specs: action.payload.co2Specs || { unit: "", value: "" },
            };
        },
        externalPriceSetError(state, _: PayloadAction<boolean>) {
            return {
                ...state,
                state: RequestStatusEnum.Error,
                calculatedPrice: { ...initialState.calculatedPrice },
                originalTotalPrice: { ...initialState.originalTotalPrice },
            };
        },
        setExternalPriceTaxLocation(state, action: PayloadAction<{ locationId: string }>) {
            return { ...state, taxLocationId: action.payload.locationId };
        },
        externalPriceReset() {
            return initialState;
        },
    },
});

export const {
    enableExternalPrice,
    startExternalPriceValueFetch,
    finishExternalPriceValueFetch,
    externalPriceSetError,
    setExternalPriceTaxLocation,
    externalPriceReset,
} = CarConfigExternalPriceSlice.actions;
